/**
 * Single source of truth for capture pipeline UI state.
 * Rule 1: never infer stage/label from percent — only uploadPercent is exposed.
 */

import { resolveCanonicalFailure } from '../failures';
import { isExhaustiveRetryInFlight } from '../stageMapping';
import { normalizeCaptureObservation } from './observation';
import {
  PIPELINE_PHASE,
  LIVE_STATUS_LABELS,
  barSublabelFromPhase,
  fromFirestoreFailedStageIndex,
  hudNotchFromPhase,
  phaseFromCanonicalStage,
  resolveViewerPrepHintLines,
  resolveUploadStatusLabel,
  segmentIndexFromPhase,
} from './stageIndex';

const MAX_RUNPOD_REDISPATCH_UI = 3;

function resolveMatcherEscalationLine(obs) {
  const message = String(obs.progress?.message || obs.progressMessage || '').trim();
  if (message.toLowerCase().includes('full image matching')) {
    return message;
  }
  return null;
}

function resolveRedispatchAttemptLine(obs) {
  const count = Number(
    obs.progress?.runpodRedispatchCount
    ?? obs.poll?.runpodRedispatchCount
    ?? 0,
  );
  if (!Number.isFinite(count) || count <= 0) return null;
  const attempt = Math.min(count + 1, MAX_RUNPOD_REDISPATCH_UI);
  return `Retrying on a new GPU (attempt ${attempt} of ${MAX_RUNPOD_REDISPATCH_UI})`;
}

function resolveUploadLabel(obs) {
  return resolveUploadStatusLabel({
    captureProfile: obs.captureProfile,
    sourceFormat: obs.sourceFormat,
  });
}

function resolvePhase(obs) {
  const status = obs.processingStatus;
  if (status === 'cancelled') return PIPELINE_PHASE.CANCELLED;
  if (obs.isPollChecking) return PIPELINE_PHASE.CHECKING;

  if (obs.isClientUploading) return PIPELINE_PHASE.CLIENT_UPLOAD;
  if (obs.isQueued) return PIPELINE_PHASE.QUEUED;

  if (status === 'failed') return PIPELINE_PHASE.FAILED;

  if (obs.isMessagingComplete) {
    if (obs.viewerRuntime?.viewerLoadPending) return PIPELINE_PHASE.VIEWER_LOAD;
    if (obs.viewerRuntime?.viewerSrcAvailable === false) return PIPELINE_PHASE.VIEWER_PREP;
    return PIPELINE_PHASE.READY;
  }

  if (obs.isViewerAssetEncoding && (
    obs.hasReconstructionArtifact
    || status === 'completed'
    || String(obs.proc?.status || '').toLowerCase() === 'completed'
  )) {
    return PIPELINE_PHASE.SOG_CONVERSION;
  }

  if (obs.viewerRuntime?.viewerLoadPending) return PIPELINE_PHASE.VIEWER_LOAD;

  // Reconstruction already produced a splat (or status is completed). Do not keep the
  // Gaussian-training HUD from a stale progress.message / processing.stage.
  if (obs.hasReconstructionArtifact || status === 'completed') {
    if (obs.isViewerAssetEncoding) return PIPELINE_PHASE.SOG_CONVERSION;
    return PIPELINE_PHASE.VIEWER_LOAD;
  }

  const fromStage = phaseFromCanonicalStage(obs.canonicalStage);
  if (fromStage) return fromStage;

  if (obs.frameFields.framesExtracted > 0) {
    return PIPELINE_PHASE.BUILDING_STRUCTURE;
  }

  if (status === 'processing' || status === 'queued') {
    return obs.workerActive ? PIPELINE_PHASE.ANALYZING_FRAMES : PIPELINE_PHASE.QUEUED;
  }

  // Hydrating / unknown — never assume gaussian training (that flash is visible on finished loads).
  return PIPELINE_PHASE.VIEWER_LOAD;
}

function resolveLiveLabel(phase, obs) {
  if (phase === PIPELINE_PHASE.CLIENT_UPLOAD) {
    return resolveUploadLabel(obs);
  }
  return LIVE_STATUS_LABELS[phase] || 'PROCESSING';
}

function resolveSubtitle(phase, obs) {
  const redispatchLine = resolveRedispatchAttemptLine(obs);
  switch (phase) {
  case PIPELINE_PHASE.QUEUED: {
    if (redispatchLine) return redispatchLine;
    const pos = obs.progress?.queuePosition ?? obs.poll?.queuePosition;
    if (pos != null && Number(pos) > 0) {
      return `Waiting for GPU (${Number(pos)} ahead)`;
    }
    return 'Waiting for GPU to start';
  }
  case PIPELINE_PHASE.STITCHING_360:
    return 'Combining lens footage into one 360 video';
  case PIPELINE_PHASE.ANALYZING_FRAMES: {
    if (redispatchLine) return redispatchLine;
    const n = obs.frameFields.framesExtracted;
    if (Number.isFinite(n) && n > 0) return `${n} images extracted`;
    return 'Extracting images from video';
  }
  case PIPELINE_PHASE.BUILDING_STRUCTURE: {
    const escalationLine = resolveMatcherEscalationLine(obs);
    if (escalationLine) return escalationLine;
    const reg = obs.frameFields.framesRegistered;
    const pool = obs.frameFields.framesMatchPool ?? obs.frameFields.framesExtracted;
    if (Number.isFinite(reg) && Number.isFinite(pool) && pool > 0) {
      return `${reg}/${pool} images matched`;
    }
    return 'Matching camera angles';
  }
  case PIPELINE_PHASE.RENDERING:
    return 'Training reconstruction';
  case PIPELINE_PHASE.SOG_CONVERSION:
    return 'Compressing for browser viewer';
  case PIPELINE_PHASE.VIEWER_PREP:
    return 'Preparing viewer';
  case PIPELINE_PHASE.VIEWER_LOAD:
    return 'Loading interactive model';
  default:
    return 'Processing on our servers';
  }
}

function resolveHints(phase, obs) {
  const redispatchLine = resolveRedispatchAttemptLine(obs);
  if (redispatchLine && (
    phase === PIPELINE_PHASE.QUEUED
    || phase === PIPELINE_PHASE.ANALYZING_FRAMES
    || phase === PIPELINE_PHASE.BUILDING_STRUCTURE
    || phase === PIPELINE_PHASE.RENDERING
  )) {
    return [redispatchLine, 'Safe to close this tab or come back later.'];
  }
  if (phase === PIPELINE_PHASE.SOG_CONVERSION || phase === PIPELINE_PHASE.VIEWER_PREP) {
    return resolveViewerPrepHintLines(obs.viewerPrepMs);
  }
  if (phase === PIPELINE_PHASE.QUEUED) {
    return ['Waiting for a free GPU worker.', 'Safe to close this tab or come back later.'];
  }
  if (phase === PIPELINE_PHASE.CLIENT_UPLOAD) {
    return ['Keep this browser tab open until the upload finishes.'];
  }
  return ['Processing continues on our servers.', 'Safe to close this tab or come back later.'];
}

function resolveFailureState(obs) {
  if (isExhaustiveRetryInFlight(obs.capture)) {
    return {
      isFailed: false,
      segmentIndex: -1,
      label: '',
      detail: '',
      code: null,
      retryOffer: 'none',
    };
  }
  const proc = obs.proc;
  const status = obs.processingStatus;
  const suppressFailure = (
    (status === 'processing' || status === 'queued')
    && obs.workerActive
    && !obs.poll?.terminalFailure
  );
  if (suppressFailure) {
    return {
      isFailed: false,
      segmentIndex: -1,
      label: '',
      detail: '',
      code: null,
      retryOffer: 'none',
    };
  }

  const failure = resolveCanonicalFailure({
    processingStatus: status,
    failedAt: proc.failedAt,
    failureDetail: proc.failureDetail,
    failureCode: proc.failureCode,
    sfmMatchingMethod: proc.sfmMatchingMethod,
    failedStageIndex: proc.failedStageIndex,
    failedPercent: proc.failedPercent,
    progressMessage: obs.progressMessage,
    polledFailedAt: obs.poll?.failedAt,
    polledFailedStageIndex: obs.poll?.failedStageIndex,
    polledFailedPercent: obs.poll?.failedPercent,
    framesRegistered: obs.frameFields.framesRegistered,
    framesExtracted: obs.frameFields.framesExtracted,
    pipelineStage: obs.canonicalStage,
    uploadError: obs.uploadCtx?.uploadError,
  });

  const segmentIndex = failure.failedStageIndex >= 0
    ? failure.failedStageIndex
    : fromFirestoreFailedStageIndex(proc.failedStageIndex);
  return {
    isFailed: status === 'failed' || failure.isFailed,
    segmentIndex,
    label: failure.failedStageLabel || failure.failedAt || 'FAILED',
    detail: failure.failureDetailText || proc.failureDetail || '',
    code: proc.failureCode || null,
    retryOffer: 'none',
  };
}

export function resolveCapturePipelineState(input = {}) {
  const obs = normalizeCaptureObservation(input);
  let phase = resolvePhase(obs);
  const failure = resolveFailureState(obs);

  if (failure.isFailed && phase !== PIPELINE_PHASE.CANCELLED) {
    phase = PIPELINE_PHASE.FAILED;
  }

  const label = phase === PIPELINE_PHASE.FAILED
    ? (failure.label || 'FAILED')
    : resolveLiveLabel(phase, obs);

  const uploadPercent = obs.isClientUploading && Number.isFinite(uploadCtxPercent(input))
    ? uploadCtxPercent(input)
    : null;

  return {
    phase,
    status: {
      label,
      segmentIndex: segmentIndexFromPhase(phase),
      hudNotch: hudNotchFromPhase(phase),
      subtitle: resolveSubtitle(phase, obs),
      hints: resolveHints(phase, obs),
      barSublabel: barSublabelFromPhase(phase),
    },
    uploadPercent,
    failure,
    counts: {
      framesExtracted: obs.frameFields.framesExtracted,
      framesRegistered: obs.frameFields.framesRegistered,
    },
    queue: {
      position: obs.progress?.queuePosition ?? obs.poll?.queuePosition ?? null,
    },
    elapsed: {
      viewerPrepMs: obs.viewerPrepMs,
    },
    flags: {
      workerActive: obs.workerActive,
      isQueueing: phase === PIPELINE_PHASE.QUEUED,
      statusChecking: phase === PIPELINE_PHASE.CHECKING,
      backendOffline: !!input.backendOffline,
      isClientUploading: obs.isClientUploading,
    },
    observation: obs,
  };
}

function uploadCtxPercent(input) {
  const uploadCtx = input.uploadCtx || {};
  const pct = uploadCtx.uploadPercent;
  return Number.isFinite(pct) ? pct : null;
}

export { PIPELINE_PHASE, LIVE_STATUS_LABELS };
